/**
 * Schedule queue hooks (TICKET-01) — pending tickets are Pegasus-native
 * dispatch rows (`pegasus_dispatch`, status='pending_schedule') in lanes
 * (`queue_lane`). This replaces ESC's pseudo-tech holding buckets: a parked
 * ticket ANNOUNCES itself (lane counts, pulse) instead of hiding on 8888.
 * Design: C:\ESCBACKUP\research\schedule-queue-design.md (locked 2026-06-03).
 *
 * Parallel-phase honesty (DEC-25): "Scheduled" here means the dispatcher
 * booked it in ESC — the ticket records the link + clears. At dispatch
 * cutover the same action creates the Pegasus dispatch directly.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "@/lib/currentUser";
import { supabase } from "@/lib/supabase";
import { checkSubResend } from "@/features/jobs/resendPrompt";
import { broadcastDataChanged } from "@/lib/boardChannel";
import type { PartEquipment } from "@/features/purchase-orders/po";

export interface QueueLane {
  key: string;
  label: string;
  position: number;
  is_active: boolean;
}

/** PARTS-02 (Seth 2026-08-26): the real-world states. needs_ordering → the
 *  parts manager either cuts a PO (ordered — automatic when the PO carries the
 *  dispatch #), pulls it off the shelf (in_stock), or learns it's backordered.
 *  arrived / in_stock both mean "schedule the return visit". */
export type PartState = "needs_ordering" | "ordered" | "in_stock" | "backordered" | "arrived";

/** Ready to book the return visit (the part is physically in hand). */
export const partReady = (s: PartState | null | undefined): boolean =>
  s === "arrived" || s === "in_stock";

/** ONCALL v8: the full structured call the answering service recorded — so the
 * Today card shows the whole picture without anyone opening the PDF. */
export interface OncallDetail {
  call_type: string | null;
  urgency_text: string | null;
  service_needed: string | null;
  caller_name: string | null;
  account_name: string | null;
  match_type: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  message: string | null;
  taken_at: string | null;
}

export interface PendingTicket {
  /** TEST-FLAG-01: a test visit (from a test job) — never messages anyone. */
  is_test?: boolean;
  id: string;
  status: "pending_schedule" | "scheduled" | "on_hold" | "cancelled" | "completed";
  lane_key: string;
  custno: string | null;
  locno: string | null;
  customer_name: string;
  address: string | null;
  city: string | null;
  lat: number | null;
  lng: number | null;
  phone: string | null;
  summary: string;
  notes: string | null;
  part_state: PartState | null;
  part_eta: string | null;
  part_number: string | null;
  /** TICKET-PO-01: the PO that's ordering this part (stamped automatically
   *  when a Pegasus PO is cut with this ticket's dispatch #). */
  part_po: string | null;
  /** PO-02: scheduling urgency for the part (critical = no heat/no cool). */
  part_priority: "critical" | "soon" | "stock" | null;
  /** FIELD-M0: the tech's data-plate / part photos (paths in job-media). */
  part_photos: string[] | null;
  /** TICKET-EQUIP-01: the unit the tech flagged the part for (model / serial). */
  part_equipment: PartEquipment | null;
  est_install_minutes: number | null;
  quoted_amount: number | null;
  payment_status: "prepaid" | "cod" | null;
  ordered_at: string | null;
  ordered_by: string | null;
  arrived_at: string | null;
  arrived_by: string | null;
  source: string;
  origin_dispatch: string | null;
  affinity_empno: string | null;
  target_date: string | null;
  target_window: string | null;
  /** SCHED-AI-02: recorded when the dispatcher books a target (asap|soon|routine). */
  urgency: "asap" | "soon" | "routine" | null;
  scheduled_dispatch: string | null;
  /** DISPATCH-NO: the Pegasus dispatch number (continues ESC's sequence once
   *  org_setting dispatch_numbering_live flips at go-live; null in the parallel
   *  phase, where the short id `ref` is the handle). */
  dispatch_no: number | null;
  /** Stamped by useResolveTicket — who cleared it and when (Recently cleared / LISTS-01 history). */
  resolved_at: string | null;
  resolved_by: string | null;
  /** ONCALL-01: after-hours intake. `tags` carries the class + triage state
   * (oncall, hold_for_office/same_day/sales/plumbing, needs_triage,
   * ran_overnight, agreement_member, agreement_tuneup, identity_review). */
  tags: string[] | null;
  source_message_id: string | null;
  source_received_at: string | null;
  /** CALL-REC-1: the call_event this dispatch was spawned from (e.g. a missed
   *  call), so the detail can play the recording inline. */
  source_call_id: string | null;
  /** ONCALL v8: full structured record of the recorded call (see OncallDetail). */
  oncall_detail: OncallDetail | null;
  /** SCHED-01: native scheduling — assigned tech + day/window + call-ahead. */
  assigned_empno: string | null;
  scheduled_date: string | null;
  scheduled_window: string | null;
  /** BOARD-PLACE: exact arrival time (HH:MM[:SS]) — sequences the job between two calls. */
  scheduled_time: string | null;
  call_ahead: boolean | null;
  call_ahead_phone: string | null;
  /** CALL-AHEAD METHOD (Seth 2026-06-24): the tech's heads-up on the way is a
   *  CALL or a TEXT (some customers want a text, not a phone call). */
  call_ahead_method: "call" | "text" | null;
  /** WHO-TO-REACH (34i87): who the heads-up reaches, who the confirmation text
   *  greets, and the email kept on the visit. Older rows: null = the account. */
  call_ahead_name?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  /** SVC-TYPE: the service type + resolved diagnostic fee. */
  service_type_name: string | null;
  service_type_id: string | null;
  po_number: string | null;
  diag_fee: number | null;
  created_by: string | null;
  created_at: string;
  /** Stamped by every quick-edit mutation; drives the "Updated … by …" line. */
  updated_at: string | null;
  /** DISPATCH-DELETE (Seth 2026-06-26): a permission-gated, RECOVERABLE delete.
   *  Set = hidden from every board/queue/customer/report read; the row + its
   *  activity log survive so an admin can restore it. Distinct from
   *  status='cancelled' (a real job that isn't happening). ESC dispatches are
   *  read-only and never carry this. */
  deleted_at: string | null;
  deleted_by: string | null;
  /** MULTI-DAY (Seth 2026-06-26): a dispatch is a VISIT of a job. When set, this
   *  dispatch belongs to a `pegasus_job` and `visit_role` labels which day it is
   *  (e.g. visit_1 / return / install). A standalone service call has job_id null. */
  job_id: string | null;
  visit_role: string | null;
}

export const PART_STATE_LABEL: Record<PartState, string> = {
  needs_ordering: "Needs ordering",
  ordered: "On order",
  in_stock: "In stock — schedule it",
  backordered: "Backordered",
  arrived: "Arrived — schedule it",
};

export const SOURCE_LABEL: Record<string, string> = {
  manual: "added by office",
  from_dispatch: "from a dispatch",
  web: "web request",
  chat: "chat",
  email: "email",
  oncall: "on-call",
  call: "phone",
  campaign: "campaign batch",
  tech_mobile: "tech (field)",
};

/** ONCALL-01: the caller's own words are the first segment of the lead's
 *  note (before " · "). Shared by the Today triage rows AND the board's
 *  ticket cards — a week later, "furnace making a grinding noise" is
 *  recognizable where "After-hours hold for office" is not (Seth 2026-06-12,
 *  the Banasik mystery ticket). */
export function oncallCallerWords(notes: string | null): string {
  if (!notes) return "";
  return (notes.split(" · ")[0] ?? "").replace(/^[“"]|[”"]$/g, "").trim();
}

export function useQueueLanes() {
  return useQuery({
    queryKey: ["queue_lanes"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<QueueLane[]> => {
      const { data, error } = await supabase
        .from("queue_lane")
        .select("*")
        .eq("is_active", true)
        .order("position");
      if (error) throw error;
      return (data ?? []) as QueueLane[];
    },
  });
}

/** All OPEN pending tickets (the strip / Today digest read this). */
export function usePendingTickets() {
  return useQuery({
    queryKey: ["pending_tickets"],
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<PendingTicket[]> => {
      const { data, error } = await supabase
        .from("pegasus_dispatch")
        .select("*")
        .eq("status", "pending_schedule")
        .is("deleted_at", null)
        // ONCALL-01: untriaged after-hours leads live on the Today on-call
        // panel only — keep them out of the general pending pile (and the
        // customer record) until a dispatcher books them.
        .not("tags", "cs", '{needs_triage}')
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as PendingTicket[];
    },
  });
}

/**
 * A single dispatch by id — the board's editable detail reads THIS (not the
 * board's click-snapshot) so an edit reflects live instead of snapping back to
 * the stale selection (Seth 2026-06-24). Mutations invalidate ["pending_ticket"].
 */
export function usePendingTicketById(id: string | null | undefined) {
  return useQuery({
    queryKey: ["pending_ticket", id],
    enabled: Boolean(id),
    staleTime: 10 * 1000,
    queryFn: async (): Promise<PendingTicket | null> => {
      const { data, error } = await supabase
        .from("pegasus_dispatch")
        .select("*")
        .eq("id", id as string)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as PendingTicket | null;
    },
  });
}

/**
 * A customer's OPEN pending tickets — the Customer 360 visibility that
 * replaces ESC's fake future-dated 9999 tickets (their reason #3: "when a
 * PLUS customer calls, the pending ticket shows on their record").
 */
export function usePendingForCustomer(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["pending_tickets_customer", custno],
    enabled: Boolean(custno),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<PendingTicket[]> => {
      const { data, error } = await supabase
        .from("pegasus_dispatch")
        .select("*")
        .eq("custno", custno as string)
        .eq("status", "pending_schedule")
        .is("deleted_at", null)
        // ONCALL-01: an after-hours lead never shows on the customer record
        // until a dispatcher confirms it's a real visit (drops needs_triage).
        .not("tags", "cs", '{needs_triage}')
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as PendingTicket[];
    },
  });
}

/**
 * A customer's SCHEDULED Pegasus jobs (SCHED-01) — assigned a tech + day +
 * window. Surfaced on the 360 so a scheduled job is visible where you booked it
 * (Seth: don't schedule something and then wonder where it went). The board view
 * of these lands with SCHED-02.
 */
export function useScheduledForCustomer(custno: string | null | undefined) {
  return useQuery({
    queryKey: ["scheduled_tickets_customer", custno],
    enabled: Boolean(custno),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<PendingTicket[]> => {
      const { data, error } = await supabase
        .from("pegasus_dispatch")
        .select("*")
        .eq("custno", custno as string)
        .eq("status", "scheduled")
        .is("deleted_at", null)
        .order("scheduled_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as PendingTicket[];
    },
  });
}

/**
 * SCHED-02 — Pegasus-scheduled jobs for a board date, to render in the per-tech
 * lanes alongside the ESC stops (parallel phase: ESC is still the live board;
 * these are Pegasus-native, marked distinctly, and become first-class at cutover).
 */
/** Local YYYY-MM-DD (matches the board's todayISO so "is the board on today?" lines up). */
function boardTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function useScheduledForBoard(dateISO: string | null | undefined) {
  return useQuery({
    queryKey: ["scheduled_board", dateISO],
    enabled: !!dateISO,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<PendingTicket[]> => {
      // CARRYOVER (Seth 2026-06-25): when the board is on TODAY, also pull any
      // EARLIER still-"scheduled" Pegasus dispatch that was never completed — it
      // rides forward so an uncompleted call never silently disappears when the
      // day rolls over (the Seth-Simonson-on-6/24 bug). Pegasus is clean + recent
      // so "every earlier open one" is safe; the ESC mirror needs a bounded window
      // (10k+ legacy 'Transfer' rows + 2013–2020 ghost opens) — see SESSION-LOG.
      // On a specific past/future day, show exactly that day.
      const isToday = !!dateISO && dateISO === boardTodayISO();
      // COMPLETED dispatches stay visible on their day (Seth 2026-08-11: he
      // completed Jeremy's call and it vanished from the board/history — ESC
      // dispatches show done all day, Pegasus ones must too). Only OPEN
      // ('scheduled') calls carry forward to today; completed ones never ride.
      const base = supabase
        .from("pegasus_dispatch")
        .select("*")
        .is("deleted_at", null);
      const { data, error } = await (isToday
        ? base.or(
            `and(status.eq.scheduled,scheduled_date.lte.${dateISO}),and(status.eq.completed,scheduled_date.eq.${dateISO})`,
          )
        : base.in("status", ["scheduled", "completed"]).eq("scheduled_date", dateISO));
      if (error) throw error;
      return (data ?? []) as PendingTicket[];
    },
  });
}

/** Derive the arrival window a precise time falls in (keeps the badge + capacity window aligned). */
export function windowForTime(t: string | null): string | null {
  if (!t) return null;
  const h = parseInt(t.slice(0, 2), 10);
  if (!Number.isFinite(h)) return null;
  if (h < 10) return "8-12";
  if (h < 12) return "10-2";
  if (h < 15) return "12-4";
  return "3-7";
}

/** BOARD-PLACE — set (or clear) a Pegasus job's exact arrival time so it sequences
 *  between two calls on the board. Setting a time also aligns its window badge. */
export function useSetDispatchTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; time: string | null }) => {
      const patch: Record<string, unknown> = { scheduled_time: v.time, updated_at: new Date().toISOString() };
      const w = windowForTime(v.time);
      if (w) patch.scheduled_window = w;
      const { error } = await supabase.from("pegasus_dispatch").update(patch).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduled_board"] });
      qc.invalidateQueries({ queryKey: ["pegasus_dispatch"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
    },
  });
}

/** Slide a Pegasus-native scheduled job on the board (Seth 2026-06-24): move it to
 *  another tech (`empno`) and/or an exact `time` (which also re-aligns the window).
 *  ESC dispatches are read-only and can't be moved this way. */
export function useMoveDispatch() {
  const qc = useQueryClient();
  const me = useCurrentUser();
  return useMutation({
    mutationFn: async (v: { id: string; empno?: string | null; time?: string | null }) => {
      const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (v.empno !== undefined) patch.assigned_empno = v.empno;
      if (v.time !== undefined) {
        // BOARD-SLIDE (Seth 2026-06-24): a drag sets the internal SEQUENCE time
        // only — the promised window NEVER auto-changes (a 12-4 dropped in front
        // of another 12-4 must not silently re-promise). The card offers the
        // window change via a confirm chip instead.
        patch.scheduled_time = v.time;
      }
      const { error } = await supabase.from("pegasus_dispatch").update(patch).eq("id", v.id);
      if (error) throw error;
      // Log a tech move into the activity stream (best-effort — never fail the move).
      if (v.empno) {
        try {
          const techs = qc.getQueryData<{ empno: string; empname: string | null }[]>(["active_techs", "03"]);
          const name = techs?.find((t) => t.empno === v.empno)?.empname ?? v.empno;
          const { data: u } = await supabase.auth.getUser();
          await supabase.from("pegasus_dispatch_event").insert({
            dispatch_id: v.id,
            kind: "tech_changed",
            summary: `Reassigned to ${name} (dragged on the board)`,
            actor: me.data?.firstName ?? u?.user?.email ?? null,
          });
        } catch {
          // ignore logging failures
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduled_board"] });
      qc.invalidateQueries({ queryKey: ["pegasus_dispatch"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["today"] });
      qc.invalidateQueries({ queryKey: ["dispatch_events"] });
      broadcastDataChanged(["scheduled_board", "pegasus_dispatch", "pending_tickets", "today"]);
    },
  });
}

/** Detect + log a reschedule after the scheduler closes (DISPATCH-DETAIL): reads
 *  the dispatch fresh, compares to the before-snapshot, and writes a
 *  "Rescheduled X → Y" event ONLY if the date / window / tech actually changed. */
export function useRescheduleLogger() {
  const log = useLogDispatchEvent();
  return async (
    jobId: string,
    before: { date: string | null; window: string | null; empno: string | null },
  ) => {
    const { data } = await supabase
      .from("pegasus_dispatch")
      .select("scheduled_date, scheduled_window, assigned_empno")
      .eq("id", jobId)
      .maybeSingle();
    if (!data) return;
    const changed =
      data.scheduled_date !== before.date ||
      data.scheduled_window !== before.window ||
      data.assigned_empno !== before.empno;
    if (!changed) return;
    const md = (d: string | null) =>
      d && /^\d{4}-\d{2}-\d{2}/.test(d) ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : d ?? "—";
    const techNote = data.assigned_empno !== before.empno ? " · tech reassigned" : "";
    log.mutate({
      dispatch_id: jobId,
      kind: "rescheduled",
      summary: `Rescheduled ${md(before.date)} ${before.window ?? "Any"} → ${md(data.scheduled_date)} ${data.scheduled_window ?? "Any"}${techNote}`,
    });
  };
}

export interface NewTicket {
  lane_key: string;
  custno?: string | null;
  locno?: string | null;
  customer_name: string;
  address?: string | null;
  city?: string | null;
  lat?: number | null;
  lng?: number | null;
  phone?: string | null;
  summary: string;
  notes?: string | null;
  part_state?: PartState | null;
  part_number?: string | null;
  part_photos?: string[] | null;
  part_equipment?: PartEquipment | null;
  /** PO-02: how urgent the SCHEDULING is once the part lands (critical = no
   *  heat / no cool; filterable on Today). */
  part_priority?: "critical" | "soon" | "stock" | null;
  est_install_minutes?: number | null;
  quoted_amount?: number | null;
  payment_status?: "prepaid" | "cod" | null;
  source?: string;
  origin_dispatch?: string | null;
  affinity_empno?: string | null;
  target_date?: string | null;
}

export function useAddTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: NewTicket) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_dispatch").insert({
        ...v,
        // Parts-lane tickets are born in the office's pile.
        part_state:
          v.lane_key === "order_parts"
            ? (v.part_state ?? "needs_ordering")
            : null,
        source: v.source ?? "manual",
        created_by: userData?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["pending_tickets"] }),
  });
}

/**
 * Bulk insert (TICKET-01d — Route & Batch "Send to schedule"): one round trip
 * for a whole day-batch; returns the created ids so the caller can offer Undo.
 */
export function useAddTickets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: NewTicket[]): Promise<string[]> => {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email ?? null;
      const payload = rows.map((v) => ({
        ...v,
        part_state:
          v.lane_key === "order_parts"
            ? (v.part_state ?? "needs_ordering")
            : null,
        source: v.source ?? "manual",
        created_by: email,
      }));
      const { data, error } = await supabase
        .from("pegasus_dispatch")
        .insert(payload)
        .select("id");
      if (error) throw error;
      return ((data ?? []) as { id: string }[]).map((r) => r.id);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["pending_tickets"] }),
  });
}

/** Bulk cancel — the Undo for a just-sent batch (no hard deletes). */
export function useCancelTickets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (ids.length === 0) return;
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("pegasus_dispatch")
        .update({
          status: "cancelled",
          resolved_at: new Date().toISOString(),
          resolved_by: userData?.user?.email ?? null,
          updated_at: new Date().toISOString(),
        })
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["pending_tickets"] }),
  });
}

/**
 * Patch a ticket (part-state steps, notes, target date). Part-state moves are
 * STAMPED with who + when — manual today; the future PO integration advances
 * `ordered` automatically when a PO is cut for the ticket (TICKET-PO-01).
 */
export function useUpdateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string } & Partial<PendingTicket>) => {
      const { id, ...patch } = v;
      const stamped: Record<string, unknown> = {
        ...patch,
        updated_at: new Date().toISOString(),
      };
      if (patch.part_state === "ordered" || patch.part_state === "arrived" || patch.part_state === "in_stock") {
        const { data: userData } = await supabase.auth.getUser();
        const email = userData?.user?.email ?? null;
        if (patch.part_state === "ordered") {
          stamped.ordered_at = new Date().toISOString();
          stamped.ordered_by = email;
        } else {
          // arrived OR pulled from stock — either way it's in hand now.
          stamped.arrived_at = new Date().toISOString();
          stamped.arrived_by = email;
        }
      }
      const { error } = await supabase
        .from("pegasus_dispatch")
        .update(stamped)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_r, v) => {
      // SUB-AUTO-RESEND: a job visit's day / window / status changed — if its
      // sub already has a work order, ask about sending the new date.
      if ("scheduled_date" in v || "scheduled_window" in v || "status" in v) void checkSubResend({ dispatchId: v.id });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      // 360 cards key off the customer-scoped queries (Seth 2026-06-23: editing a
      // ticket's notes/target on the record must refresh there too).
      qc.invalidateQueries({ queryKey: ["pending_tickets_customer"] });
      qc.invalidateQueries({ queryKey: ["scheduled_tickets_customer"] });
      // The board reads scheduled_board — editing a scheduled job's time/window
      // from its board detail must repaint the card (Seth 2026-06-24, the
      // editable Pegasus detail surface).
      qc.invalidateQueries({ queryKey: ["scheduled_board"] });
      qc.invalidateQueries({ queryKey: ["today"] });
      // The board's editable detail reads the by-id query — refresh it so an
      // edit reflects live instead of snapping back.
      qc.invalidateQueries({ queryKey: ["pending_ticket"] });
      // Two-monitor sync (Seth 2026-06-24): refetch in the other app window too.
      broadcastDataChanged([
        "pending_tickets",
        "pending_tickets_customer",
        "scheduled_tickets_customer",
        "scheduled_board",
        "today",
      ]);
    },
  });
}

/** Resolve a ticket: scheduled (optionally linking the ESC dispatch) or cancelled. */
export function useResolveTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: string;
      status: "scheduled" | "cancelled";
      scheduled_dispatch?: string | null;
    }) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("pegasus_dispatch")
        .update({
          status: v.status,
          scheduled_dispatch: v.scheduled_dispatch ?? null,
          resolved_at: new Date().toISOString(),
          resolved_by: userData?.user?.email ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      // Customer-scoped 360 cards + the board must refresh too (Seth 2026-06-23).
      qc.invalidateQueries({ queryKey: ["pending_tickets_customer"] });
      qc.invalidateQueries({ queryKey: ["scheduled_tickets_customer"] });
      qc.invalidateQueries({ queryKey: ["scheduled_board"] });
      qc.invalidateQueries({ queryKey: ["recently_cleared_tickets"] });
      // Two-monitor sync (Seth 2026-06-24): the ambient board window refetches.
      broadcastDataChanged(["pending_tickets", "pending_tickets_customer", "scheduled_tickets_customer", "scheduled_board", "recently_cleared_tickets"]);
    },
  });
}

/**
 * Office status change on a dispatch (Seth 2026-06-24): Scheduled / On hold /
 * Cancelled / Completed. Cancelling stamps resolved_at/by so it lands in
 * Recently cleared; reverting to Scheduled clears that. Field states (En route /
 * On site) arrive with the mobile app. Anything other than "scheduled" drops off
 * the active board (useScheduledForBoard filters status='scheduled').
 */
export function useSetTicketStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: string;
      status: "scheduled" | "on_hold" | "cancelled" | "completed";
    }) => {
      const patch: Record<string, unknown> = {
        status: v.status,
        updated_at: new Date().toISOString(),
      };
      if (v.status === "cancelled") {
        const { data: userData } = await supabase.auth.getUser();
        patch.resolved_at = new Date().toISOString();
        patch.resolved_by = userData?.user?.email ?? null;
      } else if (v.status === "scheduled") {
        patch.resolved_at = null;
        patch.resolved_by = null;
      }
      const { error } = await supabase
        .from("pegasus_dispatch")
        .update(patch)
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      const keys = [
        "scheduled_board",
        "pending_tickets",
        "pending_tickets_customer",
        "scheduled_tickets_customer",
        "pending_ticket",
        "today",
        "recently_cleared_tickets",
      ];
      for (const k of keys) qc.invalidateQueries({ queryKey: [k] });
      broadcastDataChanged(keys);
    },
  });
}

export interface DispatchEvent {
  id: string;
  dispatch_id: string;
  kind: string;
  summary: string;
  actor: string | null;
  created_at: string;
}

/** The activity stream for a dispatch (DISPATCH-DETAIL slice 2) — oldest first,
 *  so it reads as the story that led up to the call. */
export function useDispatchEvents(id: string | null | undefined) {
  return useQuery({
    queryKey: ["dispatch_events", id],
    enabled: Boolean(id),
    queryFn: async (): Promise<DispatchEvent[]> => {
      const { data, error } = await supabase
        .from("pegasus_dispatch_event")
        .select("*")
        .eq("dispatch_id", id as string)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DispatchEvent[];
    },
  });
}

/** Append an event to a dispatch's activity stream (stamps the signed-in actor). */
export function useLogDispatchEvent() {
  const qc = useQueryClient();
  const me = useCurrentUser();
  return useMutation({
    mutationFn: async (v: { dispatch_id: string; kind: string; summary: string }) => {
      const actor = me.data?.firstName ?? (await supabase.auth.getUser()).data?.user?.email ?? null;
      const { error } = await supabase.from("pegasus_dispatch_event").insert({
        dispatch_id: v.dispatch_id,
        kind: v.kind,
        summary: v.summary,
        actor,
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["dispatch_events", v.dispatch_id] });
    },
  });
}

/**
 * Recently cleared tickets (last 7 days) — the safety net for an accidental
 * "Called back"/"Scheduled" click (Seth 2026-06-10: "someone closes it
 * accidentally — the dispatcher needs to find it again"). Lives at the bottom
 * of the expanded strip; deep history is LISTS-01's Tickets list under Reports.
 */
export function useRecentlyCleared() {
  return useQuery({
    queryKey: ["recently_cleared_tickets"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PendingTicket[]> => {
      const since = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("pegasus_dispatch")
        .select("*")
        .in("status", ["scheduled", "cancelled"])
        .is("deleted_at", null)
        .gte("resolved_at", since)
        .order("resolved_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as PendingTicket[];
    },
  });
}

/** Put an accidentally-cleared ticket back in its lane (undo for resolve). */
export function useReopenTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("pegasus_dispatch")
        .update({
          status: "pending_schedule",
          resolved_at: null,
          resolved_by: null,
          scheduled_dispatch: null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["recently_cleared_tickets"] });
    },
  });
}

/** Group open tickets by lane (strip pills + sections). */
export function ticketsByLane(
  tickets: PendingTicket[],
): Map<string, PendingTicket[]> {
  const m = new Map<string, PendingTicket[]>();
  for (const t of tickets) {
    m.set(t.lane_key, [...(m.get(t.lane_key) ?? []), t]);
  }
  return m;
}

/** "2h ago" / "3d ago" for ticket age. */
export function ageLabel(iso: string): string {
  const mins = Math.max(0, (Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.round(mins)}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / (60 * 24))}d ago`;
}

// ───────────────────────── ONCALL-01: after-hours on-call panel ─────────────

export interface OncallSheet {
  message_id: string;
  endpoint: string | null;
  received_at: string | null;
  period_label: string | null;
  call_count: number;
  matched_count: number;
  status: string;
  parsed_at: string;
}

/** The most recent parsed after-hours sheet — the Today panel's anchor. */
export function useOncallSheet() {
  return useQuery({
    queryKey: ["oncall_sheet_latest"],
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    queryFn: async (): Promise<OncallSheet | null> => {
      const { data, error } = await supabase
        .from("oncall_sheet")
        .select("*")
        .order("received_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as OncallSheet | null;
    },
  });
}

/** Every ticket from one sheet — all states, so the panel can bucket them
 * into "needs a look", "ran overnight", and "closed". */
export function useOncallTickets(messageId: string | null | undefined) {
  return useQuery({
    queryKey: ["oncall_tickets", messageId],
    enabled: Boolean(messageId),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<PendingTicket[]> => {
      const { data, error } = await supabase
        .from("pegasus_dispatch")
        .select("*")
        .eq("source", "oncall")
        .eq("source_message_id", messageId as string)
        .is("deleted_at", null)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as PendingTicket[];
    },
  });
}

/**
 * The after-hours QUEUE (ONCALL-QUEUE, Seth 2026-06-07) — every still-OPEN
 * on-call lead across ALL nights, not just this morning's sheet. The office
 * doesn't work weekends/holidays, so Friday + Saturday + Sunday calls must all
 * still be here Monday until each is dealt with. The trick: we don't model the
 * calendar — a lead simply persists while it carries `needs_triage`, so any
 * non-working stretch (weekend, holiday, vacation) is handled for free.
 * Recently-handled leads (ran overnight / booked / closed, last ~8 days) ride
 * along for awareness; old handled ones drop off so the panel doesn't grow
 * without bound.
 */
export function useOncallQueue() {
  return useQuery({
    queryKey: ["oncall_queue"],
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<PendingTicket[]> => {
      const since = new Date(Date.now() - 8 * 24 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("pegasus_dispatch")
        .select("*")
        .eq("source", "oncall")
        .is("deleted_at", null)
        // open triage (no date bound) OR recently received (awareness window)
        .or(
          `tags.cs.{needs_triage},source_received_at.gte.${since},created_at.gte.${since}`,
        )
        .order("source_received_at", { ascending: true, nullsFirst: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as PendingTicket[];
    },
  });
}

/**
 * Resolve an on-call lead. `book` promotes it into the real pending pile (and
 * onto the customer record); `not_needed` closes it with no service-history
 * footprint; `reopen` un-does an auto "ran overnight" the system got wrong.
 * Every path is reversible — never a silent state change ([[feedback_review_before_commit]]).
 */
export function useResolveOncall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: string;
      action: "book" | "not_needed" | "reopen";
      tags: string[] | null;
    }) => {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email ?? null;
      const now = new Date().toISOString();
      const base = (v.tags ?? []).filter(
        (t) =>
          t !== "needs_triage" &&
          t !== "no_service_needed" &&
          t !== "ran_overnight",
      );
      let patch: Record<string, unknown>;
      if (v.action === "book") {
        patch = { tags: base, updated_at: now };
      } else if (v.action === "not_needed") {
        patch = {
          status: "cancelled",
          tags: [...base, "no_service_needed"],
          resolved_at: now,
          resolved_by: email,
          updated_at: now,
        };
      } else {
        patch = {
          status: "pending_schedule",
          scheduled_dispatch: null,
          tags: [...base, "needs_triage"],
          resolved_at: null,
          resolved_by: null,
          updated_at: now,
        };
      }
      const { error } = await supabase
        .from("pegasus_dispatch")
        .update(patch)
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      // BUG FIX 2026-06-11 (Seth: "Not needed does nothing"): this invalidated
      // ["oncall_tickets"] but the queue hook's key is ["oncall_queue"] — the
      // write landed, the panel never refetched. Key mismatches fail silently;
      // grep the queryKey when wiring a new invalidation.
      qc.invalidateQueries({ queryKey: ["oncall_queue"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
    },
  });
}

/** empno → display name (small table, cached) for affinity / paged techs. */
export function useEmpNames() {
  return useQuery({
    queryKey: ["emp_names"],
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await supabase
        .from("mirror_employee")
        .select("empno, empname, firstname, lastname");
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const e of (data ?? []) as {
        empno: string;
        empname: string | null;
        firstname: string | null;
        lastname: string | null;
      }[]) {
        map[e.empno] =
          e.empname ||
          `${e.firstname ?? ""} ${e.lastname ?? ""}`.trim() ||
          e.empno;
      }
      return map;
    },
  });
}
